import type { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

// organism — DWR dialog di conferma (sostituzione di confirm() / alert()
// JS native — FI-47 P2). Pattern shadcn AlertDialog skinned DWR:
// - title font-display uppercase Cinzel
// - description font-serif EB Garamond
// - confirm button bg-ink (variant=default) o bg-accent-dwr (variant=destructive)
// - cancel button ghost-btn outline DWR
//
// API: stateless. Il consumer controlla `open` via state e gestisce
// l'azione tramite `onConfirm` callback.

type ConfirmDialogVariant = 'default' | 'destructive';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void;
}

const ACTION_VARIANT_CLASSES: Record<ConfirmDialogVariant, string> = {
  default:
    'bg-ink text-paper-bright border-ink hover:bg-accent-dwr hover:border-accent-dwr font-display tracking-[0.08em] uppercase',
  destructive:
    'bg-accent-dwr text-paper-bright border-accent-dwr hover:bg-ink hover:border-ink font-display tracking-[0.08em] uppercase',
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  variant = 'default',
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-ink text-[1.2rem] font-bold tracking-[0.05em] uppercase">
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="text-ink-soft font-serif text-[0.95rem] leading-relaxed not-italic">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="font-display border-ink text-ink hover:bg-paper-soft tracking-[0.08em] uppercase">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={cn(ACTION_VARIANT_CLASSES[variant])}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
